<template>
  <div class="card h-100 tags-trends-widget">
    <div class="card-header">
      <h4 class="card-title">
        {{ widgetTitle }}
      </h4>
    </div>
    <div class="card-body">
      <div class="d-flex hide-fullscreen">
        <a
          class="btn btn-secondary btn-round btn-sm me-3 d-flex fs-5 align-items-center no-hover"
          :href="link"
          target="_blank"
        >
          {{ $t('widgetGroupPriorities.links.guide.title') }}
          <i class="bi bi-box-arrow-up-right fs-5 ms-3"></i>
        </a>
      </div>
      <div
        v-if="!enoughPeopleHaveResponded"
        class="info info-horizontal d-flex align-items-center"
      >
        <p class="info-title d-flex align-items-center m-auto mt-5 mb-5">
          {{ $t('widgetGroupActions.noActions') }}
        </p>
      </div>
      <div
        v-if="enoughPeopleHaveResponded"
        class="d-flex align-items-center ml-3 mt-5 mb-5"
      >
        <i class="bi bi-info-circle-fill fs-3 me-3 text-info"></i>
        <span v-if="(groupActions && groupActions.length) || freeTextAction">
          {{ $t('widgetGroupPriorities.description') }}
        </span>
        <span v-else>
          {{ $t('widgetGroupPriorities.noPriorities') }}
        </span>
      </div>
      <div
        v-if="enoughPeopleHaveResponded"
        :class="'group-priorities-chart ' + chartId"
        class="mb-3"
      >
        <div
          v-for="action in groupActions"
          :key="action.name"
          class="row align-text-top"
        >
          <div class="col-xl-5">
            <span class="me-3">{{ $tr(action.messages, 'title') }}</span>
          </div>
          <div class="col">
            <div
              class="progress-container progress-primary mt-1 mb-3 me-4 gap-1 position-relative d-flex align-items-center justify-content-between"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              :title="tooltipforAction(action)"
            >
              <div class="progress w-100">
                <div
                  class="progress-bar progress-bar-primary bg-primary"
                  :style="`width: ${
                    (action.numberOfPeople / maxNumberOfPeople) * 100
                  }%`"
                  role="progressbar"
                  :aria-valuenow="action.numberOfPeople"
                  aria-valuemin="0"
                  :aria-valuemax="maxNumberOfPeople"
                ></div>
              </div>
              <span class="number-of-people-in-bar text-muted">
                {{ action.numberOfPeople }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <PulsFormSwitch
        v-if="
          enoughPeopleHaveResponded && freeTextAction && freeTextAction.values
        "
        v-model="shouldShowFreeTextResponses"
        class="hide-fullscreen"
        :option="{
          title: $t('widgetGroupPriorities.freeTextResponses'),
          value: shouldShowFreeTextResponses
        }"
      />
      <div v-if="shouldShowFreeTextResponses" class="mt-3">
        <h5>{{ freeTextAction.name }}</h5>
        <div class="list-group">
          <div
            v-for="(value, text_index) in freeTextAction.values"
            :key="text_index"
            class="list-group-item action-comment"
          >
            {{ value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import { errorTypes } from '@/constants/error-types'

export const groupPrioActionsWidgetInformation = ({ colData }) => {
  return {
    requests: {
      [requestTypes.SURVEY_DETAILS]: {},
      [requestTypes.ACTIONS]: {},
      [requestTypes.CATEGORIES]: {
        filter: {
          [colData.category]: {}
        }
      },
      [requestTypes.TAGS]: {}
    }
  }
}

export const groupPrioActionsWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 2 }]
      },
      {
        cols: [{ height: 2 }]
      },
      {
        cols: [{ height: 10 }]
      }
    ]
  }
}

const GroupPrioActionsWidget = {
  name: 'GroupPrioActionsWidget',
  mixins: [commonWidgetProps],
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    actions: {
      type: Object,
      default: () => {}
    },
    categories: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      shouldShowFreeTextResponses: false
    }
  },
  mounted() {
    if (!this.enoughPeopleHaveResponded) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    }
  },
  computed: {
    categoryName() {
      return this.data.category
    },
    chartId() {
      return this.categoryName
        ? `group-priorities-chart-${this.categoryName}`
        : 'group-priorities-loading'
    },
    categoryDetails() {
      return this.surveyDetails.details.categories.find(
        (c) => c.name === this.categoryName
      )
    },
    enoughPeopleHaveResponded() {
      return this.groupActions?.length
    },
    freeTextAction() {
      const otherAction = this.actions.actions.find(
        (action) => action.actionName === 'other_action'
      )
      if (otherAction) {
        const categoryWithFreeText = otherAction.categories.find((category) =>
          category.hasOwnProperty(this.categoryName)
        )
        if (categoryWithFreeText) {
          return {
            ...categoryWithFreeText[this.categoryName],
            name: this.$tr(otherAction.messages, 'title')
          }
        }
      }
      return {}
    },
    groupActions() {
      return this.actions.actions
        .filter((action) => {
          // usually this widget only contains actions that are unique for this category
          // but this is to handle exception from that
          const categoryNames = action.categories.map((c) => Object.keys(c)[0])
          return (
            categoryNames.includes(this.data.category) &&
            action.type === 'proposal'
          )
        })
        .sort((a, b) => {
          return b.numberOfPeople - a.numberOfPeople
        })
    },
    maxNumberOfPeople() {
      return this.groupActions.reduce((res, action) => {
        if (action.numberOfPeople > res) {
          res = action.numberOfPeople
        }
        return res
      }, 0)
    },
    link() {
      return this.$t('widgetGroupPriorities.links.guide.link')
    },
    widgetTitle() {
      return this.$t('widgetGroupPriorities.title', {
        groupName: this.selectedGroupTitle
      })
    }
  },
  methods: {
    tooltipforAction(action) {
      const value = action.numberOfPeople
      return (
        this.$t('widgetGroupPriorities.people', value, +value) +
        this.$t('widgetGroupPriorities.thinkImportant', {
          category: this.$tr(action.messages, 'title')
        })
      )
    }
  }
}

export default GroupPrioActionsWidget
</script>

<style lang="scss" scoped>
.progress {
  margin-bottom: 0;
  height: 15px;
  border-radius: 5px;

  &-bar {
    border-radius: 6px;
  }
}

.number-of-people-in-bar {
  min-width: 28px;
  text-align: right;
}

.prio-tooltip {
  margin-left: 1.1em;
  background: white;
  z-index: 1001;
  font-weight: normal;
  padding: 3px 7px;
  box-shadow: 0 5px 25px 0 rgba(0, 0, 0, 0.2);
  &::before {
    border-top-color: white;
  }
}
.group-priorities-chart .ct-label.ct-vertical.ct-start {
  justify-content: flex-start !important;
  text-align: left !important;
}
.action-comment {
  border: 1px solid var(--kt-gray-600) !important;
  border-radius: var(--bs-card-border-radius);
  padding: 10px;
  color: var(--kt-black);
  background-color: #fdffc9;
  max-width: 440px;
  margin-top: 5px;
  margin-bottom: 10px;
}
</style>
